import type { ActiveAISessionEditTarget, ActiveAISessionStage } from '@/stores/uiStore';
import { shouldClearInactiveSnapshotStage } from '@/lib/ai-session/workflow/stage-transition';
/** 活动阶段只保存会话选择；创建占位尚无目标会话，临时保留其展示信息。 */
export function getAISessionStageSelection(
  stage: ActiveAISessionStage | null | undefined
): ActiveAISessionStage | null {
  if (!stage) return null;
  if (isProtectedTransientAISessionStage(stage)) return stage;
  return {
    type: stage.type,
    sessionId: stage.sessionId,
    ...(stage.skipHydration ? { skipHydration: true } : {}),
  };
}

const PENDING_STAGE_SESSION_ID = 'pending';

export const AI_SESSION_TYPE_SCREENPLAY_ALIGNMENT = 1;
export const AI_SESSION_TYPE_SCREENPLAY_ASSET_BREAKDOWN = 3;
export const AI_SESSION_TYPE_SCREENPLAY_STORYBOARD_BREAKDOWN = 4;

/** 进 C 自动开跑只允许尚未出草稿的状态；waiting_user / confirmed 再打会覆盖已核对稿。 */
const STORYBOARD_AUTOSTART_STAGE_STATUSES = new Set(['created', 'dirty', 'failed']);
const DETAIL_ASSET_STAGE_STATUSES = new Set(['draft_ready', 'dirty', 'suspended', 'confirmed']);

type SessionStageSource = {
  sessionId: string;
  sessionType: number;
  stageStatus?: string | null;
  revision?: number;
  payload?: Record<string, unknown>;
};

type WorkflowStageSnapshotSource = {
  workflowReadable?: boolean;
  currentStage: string;
  stageStatus?: string | null;
  stageCheckpoint?: string | null;
  artifactState?: string | null;
  workspaceActive: boolean;
  alignmentSessionId?: string | null;
  assetSessionId?: string | null;
  storyboardSessionId?: string | null;
};

export type WorkflowWorkspaceViewState =
  | 'hidden'
  | 'bootstrapping'
  | 'generating'
  | 'waiting_user'
  | 'reviewing_draft'
  | 'confirmed'
  | 'failed';

export type WorkflowAssistantViewState =
  | 'alignment_chat'
  | 'asset_generation_progress'
  | 'asset_confirmation_chat'
  | 'asset_edit_chat'
  | 'storyboard_generation_progress'
  | 'storyboard_confirmation_chat';

export function getConcreteAlignmentSessionId(
  activeStage: ActiveAISessionStage | null | undefined
): string | null {
  if (!activeStage || activeStage.type !== 'alignment') return null;
  const sessionId = activeStage.sessionId.trim();
  if (!sessionId || sessionId === PENDING_STAGE_SESSION_ID) return null;
  return sessionId;
}

export function getConcreteBreakdownSessionId(
  activeStage: ActiveAISessionStage | null | undefined
): string | null {
  if (
    !activeStage ||
    (activeStage.type !== 'asset_breakdown' && activeStage.type !== 'storyboard_breakdown')
  ) {
    return null;
  }
  const sessionId = activeStage.sessionId.trim();
  if (!sessionId || sessionId === PENDING_STAGE_SESSION_ID) return null;
  return sessionId;
}

export function getPendingAlignmentRestartStage(): ActiveAISessionStage {
  return {
    type: 'alignment',
    sessionId: PENDING_STAGE_SESSION_ID,
    stageStatus: 'creating',
    skipHydration: true,
  };
}

function isPendingAlignmentRestartStage(
  activeStage: ActiveAISessionStage | null | undefined
): activeStage is ActiveAISessionStage {
  return Boolean(
    activeStage?.type === 'alignment' &&
    activeStage.sessionId === PENDING_STAGE_SESSION_ID &&
    activeStage.stageStatus === 'creating' &&
    activeStage.skipHydration === true
  );
}

function isStaleWorkflowSnapshotForAlignmentSession(
  snapshot: WorkflowStageSnapshotSource | null | undefined,
  session: SessionStageSource | null | undefined
): boolean {
  return Boolean(
    snapshot?.alignmentSessionId &&
    session?.sessionType === AI_SESSION_TYPE_SCREENPLAY_ALIGNMENT &&
    snapshot.alignmentSessionId !== session.sessionId
  );
}

/** C confirmed 只在正式写入成功后产生；B confirmed 仍需继续到分镜阶段。 */
export function isCompletedStoryboardStage(
  stage: { type?: string | null; stageStatus?: string | null } | null | undefined
): boolean {
  return stage?.type === 'storyboard_breakdown' && stage.stageStatus === 'confirmed';
}

export function isCompletedWorkflowSnapshot(
  snapshot: WorkflowStageSnapshotSource | null | undefined
): boolean {
  return (
    snapshot?.currentStage === 'storyboard_breakdown' &&
    snapshot.stageStatus === 'confirmed' &&
    snapshot.artifactState === 'persisted'
  );
}

export const COMPLETED_WORKFLOW_WORKSPACE_NOTICE = {
  title: '本集拆解已完成',
  description: '正式分镜已在剧本页，无需再进入工作区。',
} as const;

/** 以会话身份限制完成信号的作用范围，不能用上一轮快照关闭新一轮分析。 */
export function isStageInWorkflowSnapshot(
  stage: ActiveAISessionStage | null | undefined,
  snapshot: WorkflowStageSnapshotSource | null | undefined
): boolean {
  if (!stage || !snapshot) return false;
  const sessionId =
    stage.type === 'alignment'
      ? snapshot.alignmentSessionId
      : stage.type === 'asset_breakdown'
        ? snapshot.assetSessionId
        : snapshot.storyboardSessionId;
  return Boolean(sessionId && sessionId === stage.sessionId);
}

export function getBreakdownStageFallbackLabel(
  activeStage: ActiveAISessionStage | null | undefined
): string | null {
  if (activeStage?.type === 'asset_breakdown') return '资产拆解会话';
  if (activeStage?.type === 'storyboard_breakdown') return '分镜拆解会话';
  return null;
}

export function shouldPromotePreparedAssetStageFromCreating({
  currentStage,
  sourceSessionId,
}: {
  currentStage: ActiveAISessionStage | null | undefined;
  sourceSessionId: string;
}): boolean {
  return Boolean(
    currentStage?.type === 'asset_breakdown' &&
    currentStage.sessionId === sourceSessionId &&
    currentStage.stageStatus === 'creating' &&
    currentStage.assetRunMode === 'checklist' &&
    currentStage.skipHydration === true
  );
}

/**
 * 判定当前 activeAISessionStage 是否处于"采纳后创建资产/分镜拆解会话"这类短暂过渡占位态
 * （stageStatus: 'creating' + skipHydration: true）。
 *
 * 处于该占位态期间，`confirmBreakdownStrategy`/`prepareStageSession` 等接口尚未返回，
 * 任何"退出/关闭工作区"操作（切换单集、关闭入口弹窗等）都不应清空 activeAISessionStage，
 * 否则接口返回后 shouldPromotePreparedAssetStageFromCreating 会判定失败，
 * 新创建的会话将不再写回全局状态，导致工作区永久性丢失（而非短暂闪烁）。
 */
export function isProtectedTransientAISessionStage(
  stage: ActiveAISessionStage | null | undefined
): boolean {
  return Boolean(stage && stage.stageStatus === 'creating' && stage.skipHydration === true);
}

export function isConfirmedStoryboardWorkspaceExit(
  dismissedStage: ActiveAISessionStage | null | undefined,
  currentStage: ActiveAISessionStage | null | undefined
): boolean {
  return Boolean(
    !currentStage &&
    dismissedStage?.type === 'storyboard_breakdown' &&
    dismissedStage.stageStatus === 'confirmed'
  );
}

export function shouldSuppressBreakdownWorkspaceAutoOpen({
  currentStage,
  dismissedStage,
  nextStage,
}: {
  currentStage: ActiveAISessionStage | null | undefined;
  dismissedStage: ActiveAISessionStage | null | undefined;
  nextStage: ActiveAISessionStage | null | undefined;
}): boolean {
  if (isConfirmedStoryboardWorkspaceExit(dismissedStage, currentStage)) {
    return Boolean(
      nextStage &&
      (nextStage.type === 'asset_breakdown' ||
        nextStage.type === 'storyboard_breakdown' ||
        nextStage.type === 'alignment')
    );
  }
  return Boolean(
    !currentStage &&
    dismissedStage &&
    nextStage &&
    (nextStage.type === 'asset_breakdown' || nextStage.type === 'storyboard_breakdown') &&
    dismissedStage.type === nextStage.type &&
    dismissedStage.sessionId === nextStage.sessionId
  );
}

export function shouldHydrateActiveAlignmentSession({
  isInlineOrOpen,
  screenplayId,
  currentSessionId,
  activeStage,
}: {
  isInlineOrOpen: boolean;
  screenplayId: string | null | undefined;
  currentSessionId: string | null | undefined;
  activeStage: ActiveAISessionStage | null | undefined;
}): boolean {
  const activeSessionId = getConcreteAlignmentSessionId(activeStage);
  return Boolean(
    isInlineOrOpen && screenplayId && activeSessionId && currentSessionId !== activeSessionId
  );
}

export function shouldHydrateActiveBreakdownSession({
  isInlineOrOpen,
  screenplayId,
  currentSessionId,
  activeStage,
}: {
  isInlineOrOpen: boolean;
  screenplayId: string | null | undefined;
  currentSessionId: string | null | undefined;
  activeStage: ActiveAISessionStage | null | undefined;
}): boolean {
  if (activeStage?.skipHydration) return false;
  const activeSessionId = getConcreteBreakdownSessionId(activeStage);
  return Boolean(
    isInlineOrOpen && screenplayId && activeSessionId && currentSessionId !== activeSessionId
  );
}

export function shouldApplyStreamStageToWorkspace({
  streamStage,
  editTarget,
}: {
  streamStage: ActiveAISessionStage | null | undefined;
  editTarget: ActiveAISessionEditTarget | null | undefined;
}): boolean {
  if (!streamStage) return false;
  if (!editTarget || editTarget.sessionId !== streamStage.sessionId) return true;
  if (editTarget.status !== 'running') return true;
  if (editTarget.kind === 'asset') {
    return streamStage.type !== 'asset_breakdown';
  }
  if (editTarget.kind === 'storyboard') {
    return streamStage.type !== 'storyboard_breakdown';
  }
  return true;
}

export function shouldPreserveBreakdownStageOnNotFound({
  activeStage,
  missingSessionId,
}: {
  activeStage: ActiveAISessionStage | null | undefined;
  missingSessionId: string | null | undefined;
}): boolean {
  if (
    !activeStage ||
    (activeStage.type !== 'asset_breakdown' && activeStage.type !== 'storyboard_breakdown')
  ) {
    return false;
  }
  if (!missingSessionId || activeStage.sessionId !== missingSessionId) {
    return false;
  }
  return activeStage.stageStatus === 'running';
}

export function shouldFinalizeActiveSessionHydration({
  cancelled,
  hydratedSessionId,
  currentSessionId,
}: {
  cancelled: boolean;
  hydratedSessionId: string | null | undefined;
  currentSessionId: string | null | undefined;
}): boolean {
  return (
    cancelled || !currentSessionId || !hydratedSessionId || hydratedSessionId === currentSessionId
  );
}

export function getRunningAssetBreakdownStage(
  sessionId: string | null | undefined
): ActiveAISessionStage | null {
  const concreteSessionId = sessionId?.trim();
  if (!concreteSessionId) return null;
  return getPendingAssetBreakdownStage(concreteSessionId);
}

/** 资产流建立前即切换到拆解布局，避免 ParseFlow 回退到旧分镜稿列表。 */
export function getPendingAssetBreakdownStage(alignmentSessionId: string): ActiveAISessionStage {
  return {
    type: 'asset_breakdown',
    sessionId: alignmentSessionId.trim(),
    stageStatus: 'running',
    assetRunMode: 'checklist',
  };
}

export function resolveSessionStageStatus(session: SessionStageSource): string | undefined {
  return session.stageStatus ?? undefined;
}

export function getActiveStageForSession(session: SessionStageSource): ActiveAISessionStage | null {
  const stageStatus = resolveSessionStageStatus(session);
  const stageRevision = session.revision ?? null;
  if (session.sessionType === AI_SESSION_TYPE_SCREENPLAY_ALIGNMENT) {
    return { type: 'alignment', sessionId: session.sessionId, stageStatus, stageRevision };
  }
  if (session.sessionType === AI_SESSION_TYPE_SCREENPLAY_ASSET_BREAKDOWN) {
    return {
      type: 'asset_breakdown',
      sessionId: session.sessionId,
      stageStatus,
      stageRevision,
      assetRunMode: getSessionAssetRunMode(stageStatus),
    };
  }
  if (session.sessionType === AI_SESSION_TYPE_SCREENPLAY_STORYBOARD_BREAKDOWN) {
    return {
      type: 'storyboard_breakdown',
      sessionId: session.sessionId,
      stageStatus,
      stageRevision,
    };
  }
  return null;
}

function getSessionAssetRunMode(
  stageStatus: string | null | undefined
): ActiveAISessionStage['assetRunMode'] {
  if (DETAIL_ASSET_STAGE_STATUSES.has(String(stageStatus ?? ''))) {
    return 'detail';
  }
  return 'checklist';
}

/**
 * 工作区已切换到另一拆解会话时，助手勿用本地旧 session 覆盖 activeStage。
 * 资产流尚未返回 type=3 会话前，允许工作区保持 asset_breakdown 阶段。
 */
export function shouldSyncAssistantStageFromSession(
  activeStage: ActiveAISessionStage | null | undefined,
  session: SessionStageSource
): boolean {
  return (
    !activeStage ||
    (activeStage.type === 'alignment' &&
      activeStage.sessionId === PENDING_STAGE_SESSION_ID &&
      session.sessionType === AI_SESSION_TYPE_SCREENPLAY_ALIGNMENT)
  );
}

export function getFallbackStageAfterAssetBreakdownFailure(
  session: SessionStageSource | null | undefined
): ActiveAISessionStage | null {
  if (!session || session.sessionType !== AI_SESSION_TYPE_SCREENPLAY_ALIGNMENT) {
    return null;
  }
  return {
    type: 'alignment',
    sessionId: session.sessionId,
    stageStatus: resolveSessionStageStatus(session),
  };
}

export function resolveReloadBreakdownStageType(
  activeStage: ActiveAISessionStage | null | undefined,
  hint?: { assetResult?: unknown }
): ActiveAISessionStage['type'] {
  if (activeStage?.type === 'asset_breakdown' || activeStage?.type === 'storyboard_breakdown') {
    return activeStage.type;
  }
  if (hint?.assetResult && typeof hint.assetResult === 'object') {
    return 'asset_breakdown';
  }
  return 'asset_breakdown';
}

export function shouldHandleBreakdownSessionUpdate({
  activeStage,
  currentSessionId,
  updatedSessionId,
}: {
  activeStage: ActiveAISessionStage | null | undefined;
  currentSessionId: string | null | undefined;
  updatedSessionId: string | null | undefined;
}): boolean {
  const nextSessionId = updatedSessionId?.trim();
  if (!nextSessionId) return false;
  if (currentSessionId && nextSessionId === currentSessionId) return true;
  if (!activeStage) return false;
  if (activeStage.type !== 'asset_breakdown' && activeStage.type !== 'storyboard_breakdown') {
    return false;
  }
  if (activeStage.sessionId === nextSessionId) {
    return true;
  }
  return activeStage.type === 'asset_breakdown' && activeStage.stageStatus === 'running';
}

export function shouldApplyBreakdownSessionLoad({
  requestedStage,
  currentStage,
}: {
  requestedStage: ActiveAISessionStage | null | undefined;
  currentStage: ActiveAISessionStage | null | undefined;
}): boolean {
  if (!requestedStage?.sessionId) return false;
  return Boolean(
    currentStage &&
    currentStage.type === requestedStage.type &&
    currentStage.sessionId === requestedStage.sessionId
  );
}

function getWorkflowBreakdownStageStatus(snapshot: WorkflowStageSnapshotSource): string {
  if (snapshot.stageStatus === 'failed') return 'failed';
  if (snapshot.stageStatus === 'running') return 'running';
  if (snapshot.stageStatus === 'confirmed') return 'confirmed';
  if (snapshot.stageStatus === 'suspended') return 'suspended';
  if (snapshot.stageStatus === 'created') return 'created';
  if (snapshot.artifactState === 'dirty') return 'dirty';
  if (
    snapshot.currentStage === 'asset_extraction' &&
    snapshot.stageCheckpoint === 'asset_checklist' &&
    snapshot.artifactState === 'frozen'
  ) {
    return 'checklist_frozen';
  }
  if (
    snapshot.currentStage === 'asset_extraction' &&
    snapshot.stageCheckpoint === 'asset_detail_draft' &&
    snapshot.artifactState === 'draft_ready'
  ) {
    return 'draft_ready';
  }
  return snapshot.stageStatus ?? 'waiting_user';
}

function getWorkflowAssetRunMode(
  snapshot: WorkflowStageSnapshotSource
): ActiveAISessionStage['assetRunMode'] {
  if (snapshot.stageCheckpoint === 'asset_detail_draft') {
    return 'detail';
  }
  return 'checklist';
}

export function resolveWorkspaceViewState(
  snapshot: WorkflowStageSnapshotSource | null | undefined
): WorkflowWorkspaceViewState {
  if (!snapshot || snapshot.workspaceActive !== true || snapshot.currentStage === 'pre_read') {
    return 'hidden';
  }
  if (snapshot.stageStatus === 'failed') return 'failed';
  if (snapshot.stageStatus === 'running') return 'generating';
  if (snapshot.stageStatus === 'confirmed') return 'confirmed';
  if (snapshot.stageStatus === 'created') return 'bootstrapping';
  if (
    snapshot.artifactState === 'draft_ready' &&
    (snapshot.stageCheckpoint === 'asset_detail_draft' ||
      snapshot.stageCheckpoint === 'storyboard_structure_draft' ||
      snapshot.stageCheckpoint === 'storyboard_detail_draft')
  ) {
    return 'reviewing_draft';
  }
  if (snapshot.stageStatus === 'waiting_user') return 'waiting_user';
  return 'bootstrapping';
}

export function resolveAssistantViewState(
  snapshot: WorkflowStageSnapshotSource | null | undefined
): WorkflowAssistantViewState {
  if (!snapshot || snapshot.currentStage === 'pre_read') {
    return 'alignment_chat';
  }
  if (snapshot.currentStage === 'asset_extraction') {
    if (snapshot.stageStatus === 'running') return 'asset_generation_progress';
    if (snapshot.stageCheckpoint === 'asset_detail_draft') return 'asset_edit_chat';
    return 'asset_confirmation_chat';
  }
  if (snapshot.currentStage === 'storyboard_breakdown') {
    if (snapshot.stageStatus === 'running') return 'storyboard_generation_progress';
    return 'storyboard_confirmation_chat';
  }
  return 'alignment_chat';
}

/**
 * 点击“采纳”后会先写入 asset_breakdown/creating 过渡占位（skipHydration），
 * 再异步创建真正的阶段会话；此时旧的 workflowSnapshot 尚未刷新，仍反映 A 阶段的
 * workspaceActive=false。若不豁免 skipHydration 占位，会被本判断误清空为 null，
 * 导致工作区退回初始态且后续“是否已推进”检测失败，永久停留在 A 阶段。
 *
 * 即使 skipHydration 已被拆解流程正常清除（如 creating → running 后台流已开始拉取），
 * 后端 `screenplay_workflows` 聚合表的 currentStage/workspaceActive 更新与
 * `prepareStageSession` 创建新阶段会话之间仍有短暂的最终一致性窗口：新会话已经
 * 写入并体现在 snapshot 的 assetSessionId/storyboardSessionId 字段里，但
 * currentStage 还停留在旧阶段、workspaceActive 仍为 false。此时 snapshot 已经
 * “认识”这个会话，不应视为“工作区应关闭”，需继续豁免，等 currentStage 真正
 * 推进后再由 resolveEffectiveStageFromWorkflowSnapshot 接管。
 */
export function shouldClearBreakdownStageWhenSnapshotInactive({
  workflowSnapshot,
  workflowWorkspaceVisible,
  activeStage,
}: {
  workflowSnapshot: WorkflowStageSnapshotSource | null | undefined;
  workflowWorkspaceVisible: boolean;
  activeStage: ActiveAISessionStage | null | undefined;
}): boolean {
  if (isCompletedWorkflowSnapshot(workflowSnapshot)) {
    return isStageInWorkflowSnapshot(activeStage, workflowSnapshot);
  }
  if (
    !workflowSnapshot ||
    workflowWorkspaceVisible ||
    !activeStage ||
    activeStage.skipHydration ||
    !isBreakdownWorkspaceStageType(activeStage)
  ) {
    return false;
  }
  const knownSessionId =
    activeStage.type === 'asset_breakdown'
      ? workflowSnapshot.assetSessionId
      : workflowSnapshot.storyboardSessionId;
  return shouldClearInactiveSnapshotStage({
    workflowWorkspaceVisible,
    activeStage,
    knownSessionId,
  });
}

export function isBreakdownWorkspaceStageType(
  stage: ActiveAISessionStage | null | undefined
): stage is ActiveAISessionStage & { type: 'asset_breakdown' | 'storyboard_breakdown' } {
  return stage?.type === 'asset_breakdown' || stage?.type === 'storyboard_breakdown';
}

/** 工作流已进入 B/C 拆解阶段时，勿用 alignment 的 createOrResume 覆盖当前 stage。 */
export function shouldDeferAlignmentSessionBootstrap(
  snapshot: WorkflowStageSnapshotSource | null | undefined,
  activeStage: ActiveAISessionStage | null | undefined,
  options?: { workflowSnapshotPending?: boolean }
): boolean {
  if (options?.workflowSnapshotPending) {
    return true;
  }
  if (getActiveStageForWorkflowSnapshot(snapshot)) {
    return true;
  }
  return isBreakdownWorkspaceStageType(activeStage);
}

export function getActiveStageForWorkflowSnapshot(
  snapshot: WorkflowStageSnapshotSource | null | undefined
): ActiveAISessionStage | null {
  if (!snapshot || isCompletedWorkflowSnapshot(snapshot)) return null;
  // workspaceActive 是工作区是否展示的唯一开关；分层状态字段只负责决定展示哪种工作区。
  if (snapshot.workspaceActive !== true) {
    return null;
  }
  const stageStatus = getWorkflowBreakdownStageStatus(snapshot);
  if (snapshot.currentStage === 'asset_extraction') {
    const sessionId = snapshot.assetSessionId?.trim();
    return sessionId
      ? {
          type: 'asset_breakdown',
          sessionId,
          stageStatus,
          assetRunMode: getWorkflowAssetRunMode(snapshot),
        }
      : null;
  }
  if (snapshot.currentStage === 'storyboard_breakdown') {
    const sessionId = snapshot.storyboardSessionId?.trim();
    return sessionId
      ? {
          type: 'storyboard_breakdown',
          sessionId,
          stageStatus,
        }
      : null;
  }
  return null;
}

/**
 * A 阶段（pre_read）的工作区始终隐藏，因此不会被 getActiveStageForWorkflowSnapshot 解析出来；
 * 刷新后若不单独恢复，助手面会退回启动面板，用户丢失正在进行的对齐会话。
 * 只读 snapshot 记录的 alignmentSessionId，不参与工作区展示判定。
 */
export function getRestorableAlignmentStageFromSnapshot(
  snapshot: WorkflowStageSnapshotSource | null | undefined
): ActiveAISessionStage | null {
  if (snapshot?.currentStage !== 'pre_read') return null;
  const sessionId = snapshot.alignmentSessionId?.trim();
  if (!sessionId) return null;
  return {
    type: 'alignment',
    sessionId,
    stageStatus: snapshot.stageStatus ?? undefined,
  };
}

export function isSameAISessionStage(
  left: ActiveAISessionStage | null | undefined,
  right: ActiveAISessionStage | null | undefined
): boolean {
  return Boolean(
    left &&
    right &&
    left.type === right.type &&
    left.sessionId === right.sessionId &&
    (left.stageStatus ?? null) === (right.stageStatus ?? null) &&
    (left.assetRunMode ?? null) === (right.assetRunMode ?? null)
  );
}

/**
 * 快照负责恢复会话身份，当前会话负责运行状态。
 * 活动阶段只保存身份；业务状态由会话与 Run 投影。
 */
export function resolveEffectiveStageFromWorkflowSnapshot({
  snapshot,
  session,
  activeStage,
  dismissedStage,
}: {
  snapshot: WorkflowStageSnapshotSource | null | undefined;
  session: SessionStageSource | null | undefined;
  activeStage: ActiveAISessionStage | null | undefined;
  dismissedStage?: ActiveAISessionStage | null | undefined;
}): ActiveAISessionStage | null {
  if (snapshot?.workflowReadable === false) return activeStage ?? null;
  const restoredStage = getActiveStageForWorkflowSnapshot(snapshot);
  if (isPendingAlignmentRestartStage(activeStage) && restoredStage) return activeStage;
  // 只有显式打开的新 A 才能证明快照属于旧轮；残留的 A session 本身没有切换阶段的资格。
  if (
    activeStage?.type === 'alignment' &&
    session?.sessionId === activeStage.sessionId &&
    isStaleWorkflowSnapshotForAlignmentSession(snapshot, session)
  )
    return activeStage;
  if (!restoredStage) {
    return snapshot?.workspaceActive && isBreakdownWorkspaceStageType(activeStage)
      ? activeStage
      : null;
  }
  if (
    isSameAISessionStage(restoredStage, dismissedStage) &&
    !isBreakdownWorkspaceStageType(restoredStage)
  )
    return null;

  if (
    activeStage?.type === restoredStage.type &&
    activeStage.sessionId === restoredStage.sessionId
  ) {
    return activeStage;
  }
  // C 已开始时，滞后的 B 快照不能回退；主动回看本轮 B 时也不抢走焦点。
  if (
    (activeStage?.type === 'storyboard_breakdown' && restoredStage.type === 'asset_breakdown') ||
    (activeStage?.type === 'asset_breakdown' &&
      restoredStage.type === 'storyboard_breakdown' &&
      activeStage.sessionId === snapshot?.assetSessionId)
  )
    return activeStage;

  // 冷启动时才用匹配的 session 初始化；失败/重试不是单调递增的状态，不能按字符串等级排序。
  const sessionStage = session ? getActiveStageForSession(session) : null;
  if (
    sessionStage?.sessionId === restoredStage.sessionId &&
    sessionStage.type === restoredStage.type
  ) {
    return {
      ...restoredStage,
      stageStatus: sessionStage.stageStatus ?? restoredStage.stageStatus,
      ...(restoredStage.type === 'asset_breakdown' && sessionStage.assetRunMode === 'detail'
        ? { assetRunMode: 'detail' }
        : {}),
    };
  }
  return restoredStage;
}

export function shouldAutoStartStoryboardBreakdown({
  autoStartRequested,
  sessionType,
  stageStatus,
}: {
  autoStartRequested: boolean | null | undefined;
  sessionType: number | null | undefined;
  stageStatus: string | null | undefined;
}): boolean {
  if (!autoStartRequested) return false;
  if (sessionType !== AI_SESSION_TYPE_SCREENPLAY_STORYBOARD_BREAKDOWN) return false;
  return STORYBOARD_AUTOSTART_STAGE_STATUSES.has(String(stageStatus ?? ''));
}

/** 已确认写入正式稿后禁止再开全量 storyboard_breakdown。失败/草稿态仍允许显式重跑。 */
export function shouldLaunchStoryboardBreakdown(stageStatus: string | null | undefined): boolean {
  return String(stageStatus ?? '') !== 'confirmed';
}

export function shouldHandleAISessionChatRequest({
  handledNonce,
  requestNonce,
}: {
  handledNonce: number | null | undefined;
  requestNonce: number | null | undefined;
}): boolean {
  return typeof requestNonce === 'number' && handledNonce !== requestNonce;
}

export function shouldSyncMessagesForChatRequest({
  skipMessageSync,
}: {
  skipMessageSync: boolean | null | undefined;
}): boolean {
  return !skipMessageSync;
}

export function shouldHydrateSessionForChatRequest({
  skipSessionHydration,
}: {
  skipSessionHydration: boolean | null | undefined;
}): boolean {
  return !skipSessionHydration;
}

export function shouldAutoStartAlignmentSession({
  activeStage,
  sessionId,
  sessionStatus,
  messagesLoaded,
  hasInteraction,
  isPreparing,
  isStreaming,
  autoStartedSessionId,
  autoStartRecorded,
}: {
  activeStage: ActiveAISessionStage | null | undefined;
  sessionId: string | null | undefined;
  sessionStatus: number | null | undefined;
  messagesLoaded: boolean;
  hasInteraction: boolean;
  isPreparing: boolean;
  isStreaming: boolean;
  autoStartedSessionId: string | null | undefined;
  autoStartRecorded: boolean;
}): boolean {
  return Boolean(
    activeStage?.type === 'alignment' &&
    sessionId &&
    messagesLoaded &&
    !hasInteraction &&
    !isPreparing &&
    !isStreaming &&
    sessionStatus !== 3 &&
    autoStartedSessionId !== sessionId &&
    !autoStartRecorded
  );
}

/**
 * 判断 AISessionAssistant 是否应在 screenplayId 变化时重置会话状态（含中断正在进行的 SSE 流）。
 *
 * 仅当"从一个真实 screenplayId 切换到另一个不同的真实 screenplayId"时才应重置：
 * - screenplayId 首次从 null/占位值解析为真实 id（previousScreenplayId 为空）不应重置，
 *   否则会在 A 阶段流式分析进行中把正在进行的 SSE 请求静默中断（abortRef.abort()），
 *   且 startAlignment 的 catch 会因 signal.aborted 直接 return，不提示任何错误，
 *   仅把已展示的总控进度冻结在中断前的那一帧。
 * - 上游查询重新验证（如 refetchOnMount）导致 screenplayId 短暂抖动为 null 后又恢复为
 *   同一个值时也不应重置（此时 nextScreenplayId 为空，直接跳过）。
 */
export function shouldResetAssistantForScreenplayChange(
  previousScreenplayId: string | null | undefined,
  nextScreenplayId: string | null | undefined
): boolean {
  return Boolean(
    previousScreenplayId && nextScreenplayId && previousScreenplayId !== nextScreenplayId
  );
}

export function shouldPreloadWorkflowSnapshot({
  screenplayId,
  isInline,
  open,
  manualLaunchStarted,
  activeStage,
}: {
  screenplayId: string | null | undefined;
  isInline: boolean;
  open: boolean;
  manualLaunchStarted: boolean;
  activeStage: ActiveAISessionStage | null | undefined;
}): boolean {
  return Boolean(screenplayId && (isInline || open || manualLaunchStarted || activeStage));
}

export function resolveAlignmentStatusAfterStream({
  streamedStatus,
  currentStatus,
  authoritativeStatus,
}: {
  streamedStatus: number | null | undefined;
  currentStatus: number | null | undefined;
  authoritativeStatus: number | null | undefined;
}): number | undefined {
  return authoritativeStatus ?? streamedStatus ?? currentStatus ?? undefined;
}
